import { Popover } from '@material-ui/core';
import { debounce, isEmpty } from 'lodash-es';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { FixedSizeList } from 'react-window';

import { IParameterOption } from '~/src/dash/components/forms/formBuilder/common';
import { CaretMiniDownIcon } from '~/src/dash/components/icons/misc-icons';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import {
  borderRadius,
  Col,
  Cols,
  fontSizing,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

const getHeight = (numItems: number) => {
  if (numItems >= 12) return 360;
  return numItems * 30 + 8;
};

const defaultOptionStyle = {
  width: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '4px 8px 4px 16px',
};

const activeOptionStyle = {
  cursor: 'default',
  color: publicaColors.lightGray,
};

const categoryStyle = {
  cursor: 'default',
  color: publicaColors.gray,
  fontSize: fontSizing.extraSmall,
  fontWeight: FONT_WEIGHT.heavy,
  backgroundColor: publicaColors.veryLightGray,
  textTransform: 'uppercase',
};

// @ts-ignore // css style issue
const getOptionStyle = (isActive: boolean, isCategory?: boolean): Properties<string | 0> => {
  switch (true) {
    case isCategory:
      return { ...defaultOptionStyle, ...categoryStyle };
    case isActive:
      return { ...defaultOptionStyle, ...activeOptionStyle };
    default:
      return defaultOptionStyle;
  }
};

interface IVirtualParameterRowProps {
  handleChange: (option: IParameterOption) => void;
  index: number;
  options: IParameterOption[];
  // @ts-ignore // css style issue
  style: Properties<string | 0>;
  selectedKey: string;
  activeKeys: Set<string>;
}

const VirtualParameterRow = ({
  index,
  style,
  options,
  handleChange,
  selectedKey,
  activeKeys,
}: IVirtualParameterRowProps) => {
  const { key, label, isCategory } = options[index];
  const [isHovered, setIsHovered] = useState(false);
  const isActive = activeKeys.has(key);
  return (
    <div
      onClick={() => (isCategory || isActive ? null : handleChange(options[index]))}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...style,
        cursor: 'pointer',
        padding: '0px',
        backgroundColor: isHovered && !isActive ? publicaColors.veryLightGray : 'inherit',
      }}
      className={`virtual-row-${key}`}
    >
      <Text small heavy={key === selectedKey} style={getOptionStyle(isActive, isCategory)}>
        {label}
      </Text>
    </div>
  );
};

interface IProps {
  index: number;
  options: IParameterOption[];
  handleSelectParameter: (idx: number, key: string) => void;
  activeKeys: Set<string>;
  selected: IParameterOption;
}

export const ParameterSelect = ({
  index,
  options,
  handleSelectParameter,
  activeKeys,
  selected,
}: IProps) => {
  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);
  const [displayedOptions, setDisplayedOptions] = useState<IParameterOption[]>([]);
  const [selectedOption, setSelectedOption] = useState(selected || {});
  const [search, setSearch] = useState('');

  const isMounted = useRef(false);

  // handler whenever prop options changes
  useEffect(() => {
    if (options) {
      setDisplayedOptions(options);
    }
  }, [JSON.stringify(options)]);

  // handler whenever the search term changes
  useEffect(() => {
    debouncedSearchUpdate();
  }, [search]);

  useEffect(() => {
    if (isMounted.current && !isEmpty(selectedOption)) {
      handleSelectParameter(index, selectedOption.key);
    } else {
      isMounted.current = true;
    }
  }, [selectedOption]);

  // when user types into the search box
  const onSearchUpdate = () => {
    // reset to show all options if search is cleared
    if (!search) {
      setDisplayedOptions(options);
      return;
    }

    // find all matched options
    const matchedOptions = options.filter(
      (item) =>
        String(item.label).includes(search) ||
        item.label.toLowerCase().includes(search) ||
        item.category.toLocaleLowerCase().includes(search),
    );
    setDisplayedOptions(matchedOptions);
  };

  const debouncedSearchUpdate = useMemo(() => {
    return debounce(onSearchUpdate, 500);
  }, [onSearchUpdate]);

  const handleOnOpen = (e: React.MouseEvent) => {
    setAnchorEl(e.currentTarget);
  };

  const handleOnClose = () => {
    unstable_batchedUpdates(() => {
      setSearch('');
      setAnchorEl(null);
    });
  };

  const handleChange = (option: IParameterOption) => {
    setSelectedOption(option);
    handleOnClose();
  };

  const listCount = displayedOptions.length;

  return (
    <>
      <Cols
        extraSmallVerticalPadding
        horizontalPadding
        onClick={handleOnOpen}
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `1px solid ${publicaColors.lightGray}`,
          borderRadius: borderRadius.small,
          width: '240px',
          cursor: 'pointer',
          transition: 'all 0.5s ease-in-out',
          background: publicaColors.white,
          height: '32px',
        }}
      >
        <Col flex>
          <Text small>{selectedOption?.label || 'Select Parameter'}</Text>
        </Col>
        <Col displayFlex style={{ marginLeft: spacing.base }}>
          <CaretMiniDownIcon color={publicaColors.gray} />
        </Col>
      </Cols>
      <Popover
        id={Boolean(anchorEl) ? 'parameter-popover' : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleOnClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            overflow: 'hidden',
            width: '320px',
            border: `1px solid ${publicaColors.lightGray}`,
            boxShadow: 'none',
          },
        }}
      >
        <Rows>
          {/* STICKY SECTION */}
          <Row
            smallPadding
            style={{
              borderBottom: `1px solid ${publicaColors.lightGray}`,
            }}
          >
            <TextInput
              enableClear
              fullWidth
              handleOnChange={(value: string) => setSearch(value.toLowerCase())}
              inputSize='small'
              isAutoFocused
              msDelay={500}
              placeholder={`Search`}
              prefix='search'
            />
          </Row>
          {/* LIST SECTION */}
          <Row noPadding>
            <FixedSizeList
              height={getHeight(listCount)}
              itemCount={listCount}
              itemSize={30}
              width={320}
            >
              {({ index, style }) =>
                VirtualParameterRow({
                  index,
                  style,
                  handleChange,
                  options: displayedOptions,
                  selectedKey: selectedOption?.key || '',
                  activeKeys,
                })
              }
            </FixedSizeList>
          </Row>
        </Rows>
      </Popover>
    </>
  );
};
